import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { IForm } from 'core-components/classes/models/IForm'
import { NotificationController } from 'core-components/library/popup'
import { calculateRedirectData, processAuthRedirect } from 'src/core/classes/helpers/authorization'
import { proxyIdentity } from 'src/core/classes/helpers/UrlHelper'
import { Rest } from 'src/core/classes/http'
import { TAuthService } from 'src/core/classes/models/IAuth'
import { useUserStore } from 'src/core/hooks/services/useUserStore'
import { setAuthError } from 'src/core/store/appSlice'
import settings from 'src/core/config/hostConfig'

export const useProcessLogin = () => {
  const [isLoginProcessing, setLoading] = useState<boolean>(false)
  const { hpLogin, identityType } = useUserStore()
  const { hash, state, pathname } = useLocation()

  const dispatch = useDispatch()

  const processLogin = useCallback(
    async (loginResult?: boolean, additionalPayload: IForm = {}) => {
      const { redirectUrl, isManualRedirect } = calculateRedirectData({
        hash,
        state,
        pathname,
        loginResult,
      })

      setLoading(true)

      try {
        const resultRedirectUrl = isManualRedirect
          ? redirectUrl
          : await hpLogin(redirectUrl, additionalPayload)
        dispatch(
          setAuthError({
            result: !resultRedirectUrl,
            message: !resultRedirectUrl
              ? 'Login failed because service is not available now. Please try again later'
              : '',
            code: !resultRedirectUrl ? 'hp-login' : '',
          })
        )

        // Without identityType, hpLogin returns prepareHpidLogin()'s HP ID v3 start URL, built from
        // our config: open it directly. processAuthRedirect would refuse it when the API is on
        // another host (VITE_USE_PROXY=false). replace, as in v2: Back from HP ID must not restore
        // this page with its spinner on. The page unloads, so loading stays on.
        if (resultRedirectUrl && !isManualRedirect && !identityType) {
          window.location.replace(resultRedirectUrl)
          return
        }
        // Only a URL our identity service returned may leave the site; a guest redirect comes
        // from the link and must stay on it
        const result = processAuthRedirect(resultRedirectUrl, { allowHPUrl: !isManualRedirect })
        if (!result) {
          setLoading(false)
        }
      } catch (err: unknown) {
        // Also catches storage errors (some privacy modes) and a missing owner from hpLogin
        NotificationController.showError({ message: (err as Error).message })
        setLoading(false)
      }
    },
    [dispatch, hash, hpLogin, identityType, pathname, state]
  )

  const login = useCallback(
    async (type: TAuthService) => {
      const url = proxyIdentity(`/authentication/${type}/url`)
      setLoading(true)

      try {
        const { redirectUrl } = calculateRedirectData({
          hash,
          state,
          pathname,
        })

        const response = await Rest.post({
          url,
          payload: {
            redirectUri: window.location.origin + `/authentication/${type}/callback`,
            pathname: redirectUrl,
          },
        })
        if (!response) {
          throw new Error('Login Empty response')
        }

        window.location.href = response
      } catch {
        setLoading(false)
        return NotificationController.showError({ message: `Something went wrong. Try again` })
      }
    },
    [hash, pathname, state]
  )

  const initiateLogin = useCallback(
    (loginResult?: boolean, additionalPayload: IForm = {}) => {
      if (settings.identityService && settings.identityType) {
        return login(settings.identityType as TAuthService)
      }
      return processLogin(loginResult, additionalPayload)
    },
    [login, processLogin]
  )

  return { processLogin, isLoginProcessing, login, initiateLogin }
}
